import {
  InsuranceName,
  translateVehicleModelType,
  VehicleModelType,
  VehicleModelWithInsurancesForD2CDto,
} from '@orcar/common';
import {
  getPrimaryColor,
  getVehicleDisplayPrice,
  IMG_COOPER,
  IMG_GRENDEUR,
  JEJURO,
} from '@orcar/common-d2c';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { optimizedImageUrl } from '@/utils/image';
import { mobonTrackProductView } from '@/utils/mobon';
import { computePromo } from '@/utils/promo';

/** 우선 노출할 차급(고급·수입·슈퍼카). 나머지 차급은 이 뒤로 채운다. */
const PRIORITY_TYPES: VehicleModelType[] = [
  VehicleModelType.LUXURY,
  VehicleModelType.IMPORTED,
  VehicleModelType.SUPERCAR,
];

const MAX_CARDS = 10;

type PremiumCar = {
  vehicleModelId: number;
  carType: VehicleModelType;
  carModel: string;
  insuranceName: string;
  imgUrl: string;
  title: string;
  typeLabel: string;
  fixedPrice: number;
  membershipPrice?: number;
  /** 정가(기준요금 원가) — 있으면 원가 취소선 + 할인율 노출. */
  originalPrice?: number;
  /** 정가 대비 할인율(%). */
  discountRate?: number;
};

export type SelectCarParams = {
  vehicleModelId: number;
  insuranceName: string;
  carType: string;
  carModel: string;
};

type PremiumCarSectionProps = {
  vehicleModelList?: VehicleModelWithInsurancesForD2CDto[];
  /** 카드 클릭 시 해당 차량으로 사전예약 → 결제 페이지 이동 */
  onSelect: (params: SelectCarParams) => void;
  isMobile: boolean;
};

/**
 * 카드에 걸 기본 보험옵션(최저가)을 고른다.
 *
 * 자차미가입(None)은 항상 최저가지만, 이걸 기본값으로 넘기면 결제 페이지에서
 * 완전면책 가입 토글이 사라진다(canAddUnlimitedOption은 일반/고급면책 base에서만
 * true). 그래서 실제 자차(일반·고급·슈퍼) 옵션 중 최저가를 우선 선택하고,
 * 자차 옵션이 전혀 없는 차량만 자차미가입으로 폴백한다.
 */
const findCheapestOption = (model: VehicleModelWithInsurancesForD2CDto) => {
  const valid = (model.insurances ?? []).filter(
    (insurance) => (getVehicleDisplayPrice(insurance) ?? -1) >= 0,
  );
  if (valid.length === 0) return null;

  const cheapestOf = (list: typeof valid) =>
    list.reduce((cheapest, insurance) =>
      (getVehicleDisplayPrice(insurance) ?? Infinity) <
      (getVehicleDisplayPrice(cheapest) ?? Infinity)
        ? insurance
        : cheapest,
    );

  const selfInsured = valid.filter(
    (insurance) => insurance.insuranceName !== InsuranceName.None,
  );

  return cheapestOf(selfInsured.length > 0 ? selfInsured : valid);
};

const fallbackImage = (type: VehicleModelType) =>
  type === VehicleModelType.LUXURY ? IMG_GRENDEUR : IMG_COOPER;

const PremiumCarSection = ({
  vehicleModelList,
  onSelect,
  isMobile,
}: PremiumCarSectionProps) => {
  const premiumCars = useMemo<PremiumCar[]>(() => {
    if (!vehicleModelList) return [];

    // 재고 있는 전 차종을 카드로 만든다(차급 제한 없음).
    const cars = vehicleModelList
      .filter((model) => model.availableCount > 0)
      .reduce<PremiumCar[]>((acc, model) => {
        const cheapest = findCheapestOption(model);
        if (!cheapest) return acc;

        // 정가(기준요금 원가) 대비 할인 — 검색 카드와 동일 computePromo.
        const sale = getVehicleDisplayPrice(cheapest) ?? 0;
        const promo = computePromo(model.referencePrice, sale);

        acc.push({
          vehicleModelId: model.id,
          carType: model.type,
          carModel: model.name,
          insuranceName: cheapest.insuranceName,
          imgUrl: model.imageUrls?.[0] || fallbackImage(model.type),
          title: model.title || model.name,
          typeLabel: translateVehicleModelType(model.type),
          fixedPrice: getVehicleDisplayPrice(cheapest) ?? 0,
          membershipPrice:
            cheapest.pricingVersion === 1
              ? undefined
              : (cheapest.userFinalPrice ?? undefined),
          originalPrice: promo.hasPromo ? promo.anchor : undefined,
          discountRate: promo.hasPromo ? promo.percentOff : undefined,
        });
        return acc;
      }, []);

    // 매 방문마다 순서를 랜덤화하되, 고급·수입·슈퍼카를 앞으로 우선 배치한다.
    const shuffle = (list: PremiumCar[]) => {
      for (let i = list.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
      return list;
    };

    const priority = shuffle(
      cars.filter((car) => PRIORITY_TYPES.includes(car.carType)),
    );
    const rest = shuffle(
      cars.filter((car) => !PRIORITY_TYPES.includes(car.carType)),
    );

    return [...priority, ...rest].slice(0, MAX_CARDS);
  }, [vehicleModelList]);

  if (premiumCars.length === 0) return null;

  return (
    <Section isMobile={isMobile}>
      <Inner>
        <Header isMobile={isMobile}>
          <SectionTitle>제주로 인기차량</SectionTitle>
        </Header>

        <CardRow>
          {premiumCars.map((car, idx) => (
            <Card
              key={`${car.vehicleModelId}-${idx}`}
              type='button'
              isMobile={isMobile}
              onClick={() => {
                // 모비온 상품(차량) 수집 — 다이나믹 리타겟팅
                mobonTrackProductView({
                  productCode: String(car.vehicleModelId),
                  productName: car.carModel,
                  price: car.originalPrice ?? car.fixedPrice,
                  dcPrice: car.originalPrice ? car.fixedPrice : undefined,
                  imageUrl: car.imgUrl,
                  topCategory: car.carType,
                });
                onSelect({
                  vehicleModelId: car.vehicleModelId,
                  insuranceName: car.insuranceName,
                  carType: car.carType,
                  carModel: car.carModel,
                });
              }}
            >
              <Thumb>
                <img src={optimizedImageUrl(car.imgUrl)} alt={car.title} />
                <TypeChip>{car.typeLabel}</TypeChip>
              </Thumb>
              <Body>
                <Name>{car.title}</Name>
                <Duration>24시간 차량요금 · 보험료 별도</Duration>
                {car.originalPrice && car.discountRate ? (
                  <PriceArea>
                    <FixedPrice>
                      {car.originalPrice.toLocaleString()}원
                    </FixedPrice>
                    <SalePrice>
                      <PromoRate>{car.discountRate}%</PromoRate>
                      {(car.membershipPrice ?? car.fixedPrice).toLocaleString()}
                      원
                    </SalePrice>
                  </PriceArea>
                ) : car.membershipPrice ? (
                  <PriceArea>
                    <FixedPrice>{car.fixedPrice.toLocaleString()}원</FixedPrice>
                    <MemberPrice>
                      <span>회원가</span>
                      {car.membershipPrice.toLocaleString()}원
                    </MemberPrice>
                  </PriceArea>
                ) : (
                  <PriceArea>
                    <SinglePrice>
                      {car.fixedPrice.toLocaleString()}원~
                    </SinglePrice>
                  </PriceArea>
                )}
              </Body>
            </Card>
          ))}
        </CardRow>
      </Inner>
    </Section>
  );
};

export default PremiumCarSection;

const Section = styled.section<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${({ isMobile }) => (isMobile ? '40px' : '80px')};
`;

const Inner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.WIDTH.PC_LAYOUT};
  padding: 0 20px;
`;

const Header = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  ${({ isMobile }) =>
    isMobile &&
    css`
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      margin-bottom: 18px;
    `}
`;

const SectionTitle = styled.h2`
  margin: 0;
  ${({ theme }) => theme.FONT.H3};
  letter-spacing: -0.03em;
`;

const CardRow = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 10px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.COLOR.GREY_04};
    border-radius: 3px;
  }
`;

const Card = styled.button<{ isMobile: boolean }>`
  flex: 0 0 auto;
  width: ${({ isMobile }) => (isMobile ? '188px' : '236px')};
  padding: 0;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_04};
  border-radius: 18px;
  background: ${({ theme }) => theme.COLOR.WHITE};
  overflow: hidden;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  scroll-snap-align: start;
`;

const Thumb = styled.div`
  position: relative;
  height: 140px;
  display: grid;
  place-items: center;
  background: linear-gradient(
    150deg,
    rgba(0, 145, 232, 0.08),
    ${({ theme }) => theme.COLOR.GREY_BG}
  );

  img {
    width: 90%;
    height: 90%;
    object-fit: contain;
  }
`;

const TypeChip = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  ${({ theme }) => theme.FONT.B3};
  font-weight: 800;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${getPrimaryColor(JEJURO)};
  padding: 4px 10px;
  border-radius: 999px;
`;

const Body = styled.div`
  padding: 14px 16px 16px;
`;

const Name = styled.h3`
  margin: 0;
  ${({ theme }) => theme.FONT.H4};
  font-size: 16px;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Duration = styled.span`
  display: block;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  margin: 4px 0 10px;
`;

const PriceArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FixedPrice = styled.span`
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_03};
  text-decoration: line-through;
`;

const MemberPrice = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  ${({ theme }) => theme.FONT.H4};
  font-weight: 800;
  color: ${({ theme }) => theme.COLOR.GREY_01};

  span {
    ${({ theme }) => theme.FONT.B3};
    font-weight: 800;
    color: ${getPrimaryColor(JEJURO)};
  }
`;

const SinglePrice = styled.span`
  ${({ theme }) => theme.FONT.H4};
  font-weight: 800;
  color: ${({ theme }) => theme.COLOR.GREY_01};
`;

const SalePrice = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  ${({ theme }) => theme.FONT.H4};
  font-weight: 800;
  color: ${({ theme }) => theme.COLOR.GREY_01};
`;

const PromoRate = styled.span`
  ${({ theme }) => theme.FONT.B3};
  font-weight: 800;
  color: ${({ theme }) => theme.COLOR.RED};
`;
